import React, { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import SEO from "../../components/common/SEO";
import {
  Mail,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Brain,
  Zap,
  Shield,
  Clock,
  TrendingUp,
  Activity,
  Sparkles,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export default function EmailVerification({ user, authToken }) {
  const [singleEmail, setSingleEmail] = useState("");
  const [includeAI, setIncludeAI] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const isAuthenticated = Boolean(authToken);

  const authHeaders = useMemo(
    () => (authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    [authToken]
  );

  const validateEmail = async () => {
    if (!singleEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    if (!isAuthenticated) {
      toast.error("Please sign in to validate emails");
      window.location.href = "/signin";
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(
        `${API}/validate-email`,
        {
          email: singleEmail.trim(),
          include_ai_analysis: includeAI,
        },
        { headers: authHeaders }
      );
      setResult(response.data);
      // console.log(response.data)
      toast.success("Validation complete!");
    } catch (error) {
      console.error("Validation failed:", error);
      const message = error?.response?.data?.detail || "Validation failed";
      toast.error(message);

      if (error?.response?.status === 401) {
        window.location.href = "/signin";
      } else if (error?.response?.status === 402) {
        window.location.href = "/pricing";
      }
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case "low":
        return "text-green-600";
      case "medium":
        return "text-yellow-600";
      case "high":
        return "text-red-600";
      default:
        return "text-slate-600";
    }
  };

  const getRiskIcon = (riskLevel) => {
    switch (riskLevel) {
      case "low":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "medium":
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case "high":
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-slate-600" />;
    }
  };

  return (
    <>
      <SEO
        title="Real-time Email Verification - VALIDMAIL"
        description="Verify email addresses in real-time with AI-powered deliverability scoring, syntax validation, DNS checks, and spam trap detection."
        keywords="email verification, real-time validation, email checker, deliverability scoring"
        canonicalUrl="https://validmail.io/product/email-verification"
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 text-sm font-medium mb-6">
              <Sparkles className="h-4 w-4" />
              Real-time Verification
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-50 mb-6">
              Verify emails{" "}
              <span className="text-sky-600 dark:text-sky-400">instantly</span>
            </h1>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Real-time email validation with AI-powered deliverability scoring,
              syntax checks, DNS verification, and spam trap detection.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-2xl p-6 border border-sky-200 dark:border-sky-800">
              <Zap className="h-10 w-10 text-sky-600 dark:text-sky-400 mb-4" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">
                Instant Results
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Get validation results in under 2 seconds with real-time SMTP checks
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6 border border-purple-200 dark:border-purple-800">
              <Brain className="h-10 w-10 text-purple-600 dark:text-purple-400 mb-4" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">
                AI Analysis
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Machine learning models detect patterns and predict deliverability
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-6 border border-emerald-200 dark:border-emerald-800">
              <Shield className="h-10 w-10 text-emerald-600 dark:text-emerald-400 mb-4" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">
                Security Checks
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Detect disposable, role-based, and dangerous email addresses
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-6 border border-amber-200 dark:border-amber-800">
              <TrendingUp className="h-10 w-10 text-amber-600 dark:text-amber-400 mb-4" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">
                99.9% Accuracy
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Industry-leading accuracy with confidence scoring for every result
              </p>
            </div>
          </div>

          {/* Validation Interface */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border-2 border-slate-200 dark:border-slate-800 p-8">
              {/* AI Toggle */}
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-3">
                  <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  <div>
                    <span className="font-semibold text-slate-900 dark:text-slate-50 block">
                      AI-Powered Security Analysis
                    </span>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {includeAI
                        ? "Enhanced analysis with threat detection & reputation scoring"
                        : "Basic validation only - no AI security analysis"}
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeAI}
                    onChange={(e) => setIncludeAI(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-900 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>

              {/* Email Input */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="email"
                    value={singleEmail}
                    onChange={(e) => setSingleEmail(e.target.value)}
                    placeholder="Enter email address to verify"
                    className="w-full pl-12 pr-4 py-4 border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 rounded-xl focus:ring-4 focus:ring-sky-300 dark:focus:ring-sky-900 focus:border-sky-500 dark:focus:border-sky-400 transition-all"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") validateEmail();
                    }}
                  />
                </div>
              </div>

              {/* Validate Button */}
              <button
                onClick={validateEmail}
                disabled={loading || !singleEmail.trim()}
                className="w-full bg-gradient-to-r from-sky-600 to-blue-600 text-white font-bold py-4 px-6 rounded-xl hover:from-sky-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    <span>
                      {includeAI ? "Analyzing with AI..." : "Validating..."}
                    </span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>
                      {includeAI ? "Verify with AI Analysis" : "Verify Email"}
                    </span>
                  </>
                )}
              </button>

              {/* Credits Info */}
              {isAuthenticated && user && (
                <div className="mt-4 flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
                  <span>Available credits: {user.credits || 0}</span>
                  <span>Cost: 1 credit per validation</span>
                </div>
              )}
            </div>

            {/* Results Display */}
            {result && (
              <div className="mt-8 bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border-2 border-slate-200 dark:border-slate-800 p-8">
                {/* Result Header */}
                <div className="flex items-center justify-between mb-6 pb-6 border-b border-slate-200 dark:border-slate-800">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
                    Validation Result
                  </h3>
                  <div className="flex items-center gap-2">
                    {getRiskIcon(result.risk_level)}
                    <span
                      className={`font-bold text-lg ${getRiskColor(
                        result.risk_level
                      )}`}
                    >
                      {result.risk_level.toUpperCase()} RISK
                    </span>
                  </div>
                </div>

                {/* Email & Status */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                      Email Address
                    </p>
                    <p className="font-mono text-lg bg-slate-100 dark:bg-slate-800 p-3 rounded-lg text-slate-900 dark:text-slate-50">
                      {result.email}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                      Validation Status
                    </p>
                    <div
                      className={`flex items-center gap-3 p-3 rounded-lg ${result.is_valid
                        ? "bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800"
                        : "bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800"
                        }`}
                    >
                      {result.is_valid ? (
                        <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                      )}
                      <span
                        className={`font-bold text-lg ${result.is_valid
                          ? "text-green-700 dark:text-green-400"
                          : "text-red-700 dark:text-red-400"
                          }`}
                      >
                        {result.is_valid ? "VALID EMAIL" : "INVALID EMAIL"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Confidence Score */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      Confidence Score
                    </p>
                    <span className="text-lg font-bold text-slate-900 dark:text-slate-50">
                      {Math.round(result.confidence_score * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-sky-600 to-blue-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${result.confidence_score * 100}%` }}
                    ></div>
                  </div>
                </div>

                {/* Validation Details */}
                <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6 mb-6">
                  <h4 className="font-bold text-slate-900 dark:text-slate-50 mb-4">
                    Validation Details
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-400">
                        Syntax Valid:
                      </span>
                      <span
                        className={
                          result.validation_details.syntax_valid
                            ? "text-green-600 dark:text-green-400 font-semibold"
                            : "text-red-600 dark:text-red-400 font-semibold"
                        }
                      >
                        {result.validation_details.syntax_valid ? "Yes" : "No"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-400">
                        DNS Valid:
                      </span>
                      <span
                        className={
                          result.validation_details.dns_valid
                            ? "text-green-600 dark:text-green-400 font-semibold"
                            : "text-red-600 dark:text-red-400 font-semibold"
                        }
                      >
                        {result.validation_details.dns_valid ? "Yes" : "No"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-400">
                        Domain:
                      </span>
                      <span className="font-mono text-slate-900 dark:text-slate-50">
                        {result.validation_details.domain}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-400">
                        Disposable:
                      </span>
                      <span
                        className={
                          result.validation_details.is_disposable
                            ? "text-red-600 dark:text-red-400 font-semibold"
                            : "text-green-600 dark:text-green-400 font-semibold"
                        }
                      >
                        {result.validation_details.is_disposable ? "Yes" : "No"}
                      </span>
                    </div>
                  </div>

                  {/* Errors */}
                  {(result.validation_details.syntax_errors?.length > 0 ||
                    result.validation_details.dns_errors?.length > 0) && (
                      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <p className="font-semibold text-red-700 dark:text-red-400 mb-2">
                          Issues Found:
                        </p>
                        <ul className="space-y-1 text-red-600 dark:text-red-400">
                          {[
                            ...result.validation_details.syntax_errors,
                            ...result.validation_details.dns_errors,
                          ].map((error, index) => (
                            <li key={index} className="text-sm">
                              • {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                </div>


                {result.ai_analysis && (
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 
                  border-2 border-purple-200 dark:border-purple-800 rounded-xl p-6">

                    <div className="flex items-center gap-3 mb-4">
                      <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                      <h4 className="font-bold text-purple-900 dark:text-purple-300 text-lg">
                        AI-Powered Security Analysis
                      </h4>
                    </div>

                    <div className="prose dark:prose-invert max-w-none 
                prose-h3:text-purple-700 dark:prose-h3:text-purple-300
                prose-strong:text-slate-900 dark:prose-strong:text-white
                prose-li:marker:text-purple-600 dark:prose-li:marker:text-purple-400
                prose-code:bg-slate-200 dark:prose-code:bg-slate-800
                prose-code:px-1 prose-code:py-0.5 prose-code:rounded">

                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw, rehypeSanitize]}
                      >
                        {result.ai_analysis}
                      </ReactMarkdown>

                    </div>

                  </div>
                )}


              </div>
            )}
          </div>

          {/* CTA Section */}
          {!isAuthenticated && (
            <div className="mt-16 bg-gradient-to-r from-sky-600 to-blue-600 rounded-3xl p-12 text-center text-white shadow-2xl">
              <h2 className="text-3xl font-bold mb-4">
                Start validating emails today
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Get 100 free credits. No credit card required.
              </p>
              <button
                onClick={() => (window.location.href = "/signin")}
                className="px-8 py-4 rounded-full text-lg font-bold bg-white text-sky-600 hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl hover:scale-105 transform duration-200"
              >
                Sign Up Free
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
